import React, { useEffect, useRef, useState } from "react"
import { withRouter, Link } from "react-router-dom"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap"
import BootstrapTable from "react-bootstrap-table-next"
import TableLoader from "components/Common/TableLoader"
import { numberWithCommas } from "helpers/helpers"
import {
  getAllBonusCampaignsCall,
  getBonusCampaignLookupsCall,
  getAffiliatesCall,
  activateBonusCampaignCall,
  pauseBonusCampaignCall,
  deleteBonusCampaignCall,
} from "helpers/repository"
import { STATUS_META, mapCampaignApiResponse, filterOutExcludedCountries } from "./data"
import { formatDate, formatDateRange } from "./utils"
import ConfirmDialog from "./ConfirmDialog"
import StatusBadge from "./StatusBadge"
import { useCanManageAffiliateBonuses } from "./permissions"

const SORT_OPTIONS = [
  { value: "newest", label: "Sort: Newest" },
  { value: "oldest", label: "Sort: Oldest" },
]

const PER_PAGE = 20
const GROUP_SIZE = 3

function Table(props) {
  const canEdit = useCanManageAffiliateBonuses()

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [countryFilter, setCountryFilter] = useState("")
  const [brandFilter, setBrandFilter] = useState("")
  const [affiliateFilter, setAffiliateFilter] = useState("")
  const [sortOption, setSortOption] = useState("newest")
  const [page, setPage] = useState(1)

  const [campaigns, setCampaigns] = useState([])
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)

  const [countryOptions, setCountryOptions] = useState([])
  const [brandOptions, setBrandOptions] = useState([])
  const [affiliateOptions, setAffiliateOptions] = useState([])

  const [confirm, setConfirm] = useState(null)
  const closeConfirm = () => setConfirm(null)

  // A Set (not a single id) so acting on one row never clobbers another
  // row's in-flight state — with a single id, starting a second row's action
  // while the first is still pending would overwrite it, and whichever
  // request settled first would clear BOTH rows' spinners regardless of
  // whether the other one had actually finished.
  const [actioningIds, setActioningIds] = useState(() => new Set())
  const startActioning = id =>
    setActioningIds(prev => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  const stopActioning = id =>
    setActioningIds(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })

  // Filter/sort lookups — fetched once.
  useEffect(() => {
    let cancelled = false
    Promise.all([
      getBonusCampaignLookupsCall(),
      getAffiliatesCall({ per_page: 100 }),
    ])
      .then(([lookupsRes, affiliatesRes]) => {
        if (cancelled) return
        const lookups = lookupsRes?.data?.data ?? {}
        setCountryOptions(filterOutExcludedCountries(lookups.countries ?? []))
        setBrandOptions(lookups.brands ?? [])
        const affiliateRows =
          affiliatesRes?.data?.data?.data ??
          affiliatesRes?.data?.data ??
          affiliatesRes?.data ??
          []
        setAffiliateOptions(
          (Array.isArray(affiliateRows) ? affiliateRows : []).map(a => ({
            id: a.id,
            // /admin/affiliates has no display-name field — affiliates are
            // identified by their referral code, not a company/person name.
            name:
              a.username ??
              a.referral_code ??
              a.phone_number ??
              `Affiliate #${a.id}`,
          }))
        )
      })
      .catch(err => console.error(err))
    return () => {
      cancelled = true
    }
  }, [])

  // Debounce the search box so it doesn't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [search])

  // Campaign list — refetches whenever page, sort, search, or any filter changes.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const params = { page, per_page: PER_PAGE, sort: sortOption }
    if (debouncedSearch) params.search = debouncedSearch
    if (countryFilter) params.country_id = countryFilter
    if (brandFilter) params.brand_id = brandFilter
    if (affiliateFilter) params.affiliate_id = affiliateFilter

    getAllBonusCampaignsCall(params)
      .then(res => {
        if (cancelled) return
        const payload = res?.data?.data ?? res?.data ?? {}
        const rows = Array.isArray(payload) ? payload : payload.data ?? []
        setCampaigns(rows.map(mapCampaignApiResponse))
        setTotalPages(Math.max(1, Number(payload.last_page) || 1))
        setTotalCount(Number(payload.total) || rows.length)
      })
      .catch(err => {
        if (cancelled) return
        console.error(err)
        toastr.error("Could not load bonus campaigns.")
        setCampaigns([])
        setTotalPages(1)
        setTotalCount(0)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
        setInitialLoad(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    page,
    sortOption,
    debouncedSearch,
    countryFilter,
    brandFilter,
    affiliateFilter,
  ])

  // Changing a filter/sort jumps back to page 1 in the same state update, so
  // only one request fires for the new combination (not one for the old page,
  // then another once page resets).
  const handleSearchChange = e => setSearch(e.target.value)
  const handleCountryChange = e => {
    setCountryFilter(e.target.value)
    setPage(1)
  }
  const handleBrandChange = e => {
    setBrandFilter(e.target.value)
    setPage(1)
  }
  const handleAffiliateChange = e => {
    setAffiliateFilter(e.target.value)
    setPage(1)
  }
  const handleSortChange = e => {
    setSortOption(e.target.value)
    setPage(1)
  }

  const scrollRef = useRef(null)
  const dragState = useRef({
    dragging: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  })

  const handleDragStart = e => {
    const el = scrollRef.current
    if (!el) return
    dragState.current = {
      dragging: true,
      startX: e.pageX,
      startScrollLeft: el.scrollLeft,
      moved: false,
    }
    el.classList.add("is-dragging")
  }

  const handleDragMove = e => {
    const el = scrollRef.current
    if (!el || !dragState.current.dragging) return
    const delta = e.pageX - dragState.current.startX
    if (Math.abs(delta) > 3) dragState.current.moved = true
    el.scrollLeft = dragState.current.startScrollLeft - delta
  }

  const endDrag = () => {
    const el = scrollRef.current
    dragState.current.dragging = false
    if (el) el.classList.remove("is-dragging")
  }

  const swallowClickIfDragged = e => {
    if (dragState.current.moved) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const handleView = (row, e) => {
    e.preventDefault()
    props.history.push(`/affiliate/bonus-configuration/view?id=${row.id}`)
  }
  const handleEdit = (row, e) => {
    e.preventDefault()
    // The query string must go in `search`, not appended to `pathname` --
    // history.push({pathname}) takes pathname literally (no re-parsing), so a
    // pathname of ".../edit?id=7" no longer exact-matches the route's plain
    // ".../edit" path and <Switch> silently renders nothing on client-side nav.
    props.history.push({
      pathname: `/affiliate/bonus-configuration/edit`,
      search: `?id=${row.id}`,
      state: { campaign: row },
    })
  }

  const handleActivate = row => {
    startActioning(row.id)
    activateBonusCampaignCall(row.id)
      .then(res => {
        setCampaigns(prev =>
          prev.map(c => (c.id === row.id ? { ...c, status: "Active" } : c))
        )
        toastr.success(res?.data?.message || "Campaign activated.")
      })
      .catch(err => {
        console.error(err)
        toastr.error(
          err?.response?.data?.message ||
            "Could not activate the campaign. Please try again."
        )
      })
      .finally(() => stopActioning(row.id))
  }

  const handlePause = row => {
    startActioning(row.id)
    pauseBonusCampaignCall(row.id)
      .then(res => {
        setCampaigns(prev =>
          prev.map(c => (c.id === row.id ? { ...c, status: "Paused" } : c))
        )
        toastr.success(res?.data?.message || "Campaign paused.")
        closeConfirm()
      })
      .catch(err => {
        console.error(err)
        toastr.error(
          err?.response?.data?.message ||
            "Could not pause the campaign. Please try again."
        )
        closeConfirm()
      })
      .finally(() => stopActioning(row.id))
  }

  const handlePauseToggle = (row, e) => {
    e.preventDefault()
    if (row.status === "Active") {
      setConfirm({
        title: `Pause ${row.name}?`,
        body: "New registrations will stop receiving this bonus until you resume it.",
        confirmLabel: "Pause",
        onConfirm: () => handlePause(row),
      })
    } else {
      handleActivate(row)
    }
  }

  const handleDelete = (row, e) => {
    e.preventDefault()
    setConfirm({
      title: `Delete ${row.name}?`,
      body: "This permanently removes the campaign and its configuration. This cannot be undone.",
      confirmLabel: "Delete",
      onConfirm: () => {
        startActioning(row.id)
        deleteBonusCampaignCall(row.id)
          .then(res => {
            setCampaigns(prev => prev.filter(c => c.id !== row.id))
            setTotalCount(prev => Math.max(0, prev - 1))
            toastr.success(res?.data?.message || "Campaign deleted.")
            closeConfirm()
          })
          .catch(err => {
            console.error(err)
            toastr.error(
              err?.response?.data?.message ||
                "Could not delete the campaign. Please try again."
            )
            closeConfirm()
          })
          .finally(() => stopActioning(row.id))
      },
    })
  }

  const columns = [
    {
      dataField: "name",
      text: "Campaign",
      formatter: (cell, row) => (
        <>
          <div className="fw-bold">{row.name}</div>
          <div className="text-muted font-size-12">{row.subtitle}</div>
        </>
      ),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell, row) => (
        <StatusBadge status={row.status} meta={STATUS_META} />
      ),
    },
    {
      dataField: "bonusType",
      text: "Bonus Type",
    },
    {
      dataField: "amount",
      text: "Amount",
      formatter: (cell, row) => <span className="fw-bold">{row.amount}</span>,
    },
    {
      dataField: "country",
      text: "Country",
    },
    {
      dataField: "affiliate",
      text: "Affiliate",
    },
    {
      dataField: "period",
      text: "Period",
      formatter: (cell, row) => (
        <>{formatDateRange(row.startDate, row.endDate)}</>
      ),
    },
    {
      dataField: "claims",
      text: "Claims",
      formatter: (cell, row) => <>{numberWithCommas(row.claims)}</>,
    },
    {
      dataField: "remaining",
      text: "Remaining",
      formatter: (cell, row) => <>{numberWithCommas(row.remaining)}</>,
    },
    {
      dataField: "lastUpdated",
      text: "Last Updated",
      formatter: (cell, row) => <>{formatDate(row.lastUpdated)}</>,
    },
    {
      dataField: "action",
      isDummyField: true,
      text: "Actions",
      // eslint-disable-next-line react/display-name
      formatter: (cellContent, row) => {
        const isActioning = actioningIds.has(row.id)
        return (
          <div className="d-flex gap-2 justify-content-end">
            <Link
              to="#"
              className="ab-icon-btn"
              onClick={e => handleView(row, e)}
              id={`viewtooltip-${row.id}`}
            >
              <i className="mdi mdi-eye-outline" />
              <UncontrolledTooltip
                placement="top"
                target={`viewtooltip-${row.id}`}
              >
                View
              </UncontrolledTooltip>
            </Link>
            {canEdit && (
              <>
                <Link
                  to="#"
                  className="ab-icon-btn"
                  onClick={e => handleEdit(row, e)}
                  id={`edittooltip-${row.id}`}
                >
                  <i className="mdi mdi-pencil-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`edittooltip-${row.id}`}
                  >
                    Edit
                  </UncontrolledTooltip>
                </Link>
                <Link
                  to="#"
                  className="ab-icon-btn"
                  onClick={e => handlePauseToggle(row, e)}
                  id={`toggletooltip-${row.id}`}
                >
                  <i
                    className={`mdi ${
                      row.status === "Active" ? "mdi-pause" : "mdi-play"
                    }`}
                  />
                  <UncontrolledTooltip
                    placement="top"
                    target={`toggletooltip-${row.id}`}
                  >
                    {row.status === "Active" ? "Pause" : "Activate"}
                  </UncontrolledTooltip>
                </Link>
                <Link
                  to="#"
                  className={`ab-icon-btn ab-icon-btn-danger`}
                  onClick={e => handleDelete(row, e)}
                  id={`deletetooltip-${row.id}`}
                >
                  <i className="mdi mdi-trash-can-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`deletetooltip-${row.id}`}
                  >
                    Delete
                  </UncontrolledTooltip>
                </Link>
              </>
            )}
          </div>
        )
      },
    },
  ]

  const groupStart = Math.floor((page - 1) / GROUP_SIZE) * GROUP_SIZE + 1
  const pageSlots = Array.from({ length: GROUP_SIZE }, (_, i) => groupStart + i).filter(
    n => n <= totalPages
  )
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PER_PAGE + 1
  const rangeEnd =
    totalCount === 0 ? 0 : (page - 1) * PER_PAGE + campaigns.length

  return (
    <>
      <Card className="ab-card">
        <style>{`
        .ab-card { border-radius: 10px; overflow: hidden; }

        .table-drag-scroll { cursor: grab; scrollbar-width: none; -ms-overflow-style: none; }
        .table-drag-scroll::-webkit-scrollbar { display: none; }
        .table-drag-scroll.is-dragging { cursor: grabbing; user-select: none; }
        .table-drag-scroll.is-dragging * { user-select: none; }

        .ab-icon-btn {
          width: 30px;
          height: 30px;
          border: 1px solid #e2e5ec;
          background: #fff;
          border-radius: 8px;
          color: #74788d;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          flex-shrink: 0;
        }
        .ab-icon-btn:hover { border-color: #556ee6; color: #556ee6; }
        .ab-icon-btn-danger:hover { border-color: #f46a6a; color: #f46a6a; }
        .ab-icon-btn.disabled { opacity: .5; pointer-events: none; }

        .ab-search-box { position: relative; }
        .ab-search-box .form-control { border-radius: 4px; padding-left: 34px; }
        .ab-search-box .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 14px;
          line-height: 1;
          color: #adb5bd;
        }

        .affiliate-bonuses-pagination .pagination { gap: 6px; }
        .affiliate-bonuses-pagination .page-item .page-link {
          border: 1px solid #e2e5ec;
          border-radius: 8px;
          min-width: 32px;
          height: 32px;
          margin: 0;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #495057;
          background-color: #fff;
          box-shadow: none;
        }
        .affiliate-bonuses-pagination .page-item:not(.active):not(.disabled) .page-link:hover {
          border-color: #556ee6;
          color: #556ee6;
        }
        .affiliate-bonuses-pagination .page-item.active .page-link {
          border-color: #556ee6;
          background-color: #556ee6;
          color: #fff;
        }
        .affiliate-bonuses-pagination .page-item.disabled .page-link {
          color: #adb5bd;
          background-color: #fff;
        }

        .table-drag-scroll table.table-hover tbody tr.mytable-hover-row:hover td,
        .table-drag-scroll table.table-hover tbody tr.mytable-hover-row:hover th {
          /* Bootstrap 5's own .table-hover paints its hover tint via an inset
             box-shadow on the cell, separate from background-color — cancel
             it so only this flat tint shows, matching the audit log table. */
          box-shadow: none;
          background-color: rgba(85, 110, 230, 0.06);
        }
        .ab-loading-overlay {
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0.65);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
        }
      `}</style>
        <CardBody>
          <Row className="mb-2">
            <Col sm="12">
              <Row className="g-2 align-items-center">
                <Col>
                  <div className="ab-search-box w-100">
                    <Input
                      type="text"
                      placeholder="Search campaign name..."
                      aria-label="Search campaigns"
                      value={search}
                      onChange={handleSearchChange}
                    />
                    <i className="bx bx-search-alt search-icon" />
                  </div>
                </Col>
                <Col sm="3">
                  <Input
                    type="select"
                    aria-label="Filter by country"
                    value={countryFilter}
                    onChange={handleCountryChange}
                  >
                    <option value="">All Countries</option>
                    {countryOptions.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col sm="3">
                  <Input
                    type="select"
                    aria-label="Filter by brand"
                    value={brandFilter}
                    onChange={handleBrandChange}
                  >
                    <option value="">All Brands</option>
                    {brandOptions.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col sm="3">
                  <Input
                    type="select"
                    aria-label="Filter by affiliate"
                    value={affiliateFilter}
                    onChange={handleAffiliateChange}
                  >
                    <option value="">All Affiliates</option>
                    {affiliateOptions.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col sm="3">
                  <Input
                    type="select"
                    aria-label="Sort campaigns"
                    value={sortOption}
                    onChange={handleSortChange}
                  >
                    {SORT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
            </Col>
          </Row>

          {initialLoad ? (
            <TableLoader />
          ) : (
            <>
              <div style={{ position: "relative" }} aria-busy={loading}>
                {loading && (
                  <div
                    className="ab-loading-overlay"
                    role="status"
                    aria-live="polite"
                  >
                    <Spinner color="primary" />
                    <span className="visually-hidden">Loading campaigns…</span>
                  </div>
                )}

                {campaigns.length === 0 ? (
                  <div className="text-center py-5">
                    <div
                      className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: 64, height: 64 }}
                    >
                      <i
                        className="mdi mdi-bullhorn-outline text-primary"
                        style={{ fontSize: "28px" }}
                      />
                    </div>
                    <h5 className="mb-2">No bonus campaigns found</h5>
                    <p
                      className="text-muted mx-auto mb-3"
                      style={{ maxWidth: 360 }}
                    >
                      {countryFilter || brandFilter || affiliateFilter
                        ? "Try adjusting your filters to find what you’re looking for."
                        : "Create your first Affiliate Registration Bonus to start rewarding referred sign-ups."}
                    </p>
                    {canEdit && (
                      <Button
                        color="primary"
                        className="btn-rounded"
                        onClick={() =>
                          props.history.push(
                            "/affiliate/bonus-configuration/create"
                          )
                        }
                      >
                        <i className="mdi mdi-plus me-1" />
                        New Campaign
                      </Button>
                    )}
                  </div>
                ) : (
                  <div
                    className="table-responsive table-drag-scroll"
                    ref={scrollRef}
                    onMouseDown={handleDragStart}
                    onMouseMove={handleDragMove}
                    onMouseUp={endDrag}
                    onMouseLeave={endDrag}
                    onClickCapture={swallowClickIfDragged}
                  >
                    <BootstrapTable
                      keyField="id"
                      responsive
                      bordered={false}
                      striped={false}
                      hover
                      classes={"table align-middle table-nowrap table-check"}
                      headerWrapperClasses={"table-light"}
                      columns={columns}
                      data={campaigns}
                      rowClasses={"mytable-hover-row"}
                    />
                  </div>
                )}
              </div>

              <Row className="align-items-center mt-2">
                <Col sm="6">
                  <div className="text-muted" aria-live="polite">
                    Showing {rangeStart}-{rangeEnd} of {totalCount} campaigns
                  </div>
                </Col>
                <Col sm="6">
                  <nav aria-label="Campaigns pagination">
                    <ul className="pagination affiliate-bonuses-pagination justify-content-end mb-0">
                      <li
                        className={`page-item ${page === 1 ? "disabled" : ""}`}
                      >
                        <button
                          type="button"
                          className="page-link"
                          aria-label="Previous page"
                          disabled={page === 1}
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                        >
                          ‹
                        </button>
                      </li>
                      {pageSlots.map(n => {
                        const disabled = n > totalPages
                        return (
                          <li
                            key={n}
                            className={`page-item ${
                              n === page ? "active" : ""
                            } ${disabled ? "disabled" : ""}`}
                          >
                            <button
                              type="button"
                              className="page-link"
                              disabled={disabled}
                              aria-current={n === page ? "page" : undefined}
                              aria-label={`Page ${n}`}
                              onClick={() => setPage(n)}
                            >
                              {n}
                            </button>
                          </li>
                        )
                      })}
                      <li
                        className={`page-item ${
                          page === totalPages ? "disabled" : ""
                        }`}
                      >
                        <button
                          type="button"
                          className="page-link"
                          aria-label="Next page"
                          disabled={page === totalPages}
                          onClick={() =>
                            setPage(p => Math.min(totalPages, p + 1))
                          }
                        >
                          ›
                        </button>
                      </li>
                    </ul>
                  </nav>
                </Col>
              </Row>
            </>
          )}
        </CardBody>
      </Card>
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={closeConfirm}
      />
    </>
  )
}

export default withRouter(Table)
